import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronRight,
  ChevronLeft,
  Instagram,
  RefreshCcw,
} from "lucide-react";
import { useContent, useSeo } from "@/lib/contentStore";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { Button } from "@/components/ui/button";
import ContactDialog from "@/components/ContactDialog";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function SmartImage({
  src,
  alt = "",
  className = "",
  fallbackClassName = "",
  loading = "lazy",
}) {
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return <div aria-hidden className={cn("bg-[#f1ece4]", className, fallbackClassName)} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

function Breadcrumbs({ items }) {
  return (
    <div className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-zinc-500 sm:mb-6">
      {items.map((it, idx) => (
        <React.Fragment key={it.to || it.label || idx}>
          {idx !== 0 && <ChevronRight className="h-3.5 w-3.5" />}
          {it.to ? (
            <Link to={it.to} className="break-words hover:text-zinc-900">
              {it.label}
            </Link>
          ) : (
            <span className="break-words text-zinc-900">{it.label}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function HeroSlider() {
  const { content } = useContent();
  const slides = content.home.slides;
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    if (!slides.length) return undefined;
    const t = setInterval(() => setActive((p) => (p + 1) % slides.length), 6500);
    return () => clearInterval(t);
  }, [slides.length]);

  if (!slides.length) {
    return null;
  }

  return (
    <section className="relative mb-12 overflow-hidden rounded-2xl border border-[#d9d2c6] shadow-[0_20px_45px_rgba(31,26,23,0.12)]">
      <div className="relative h-[300px] w-full sm:h-[380px] md:h-[520px]">
        {slides.map((s, idx) => {
          const isActive = idx === active;
          return (
            <div
              key={s.id}
              className={cn(
                "absolute inset-0 transition-opacity duration-700",
                isActive ? "opacity-100" : "opacity-0"
              )}
            >
              <img
                src={s.image}
                alt={s.title || ""}
                className="h-full w-full object-cover"
                loading={idx === 0 ? "eager" : "lazy"}
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/18 to-black/45" />

              <div className="absolute inset-0">
                <div className="mx-auto flex h-full max-w-6xl items-center px-4">
                  <div className="max-w-xl">
                    <div className="display-title text-3xl font-semibold leading-tight text-white drop-shadow-[0_4px_16px_rgba(0,0,0,0.35)] sm:text-4xl md:text-[52px]">
                      {s.title}
                    </div>
                    {s.subtitle ? (
                      <div className="mt-2 max-w-lg text-sm text-white/95 sm:mt-3 sm:text-base">
                        {s.subtitle}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => setActive((p) => (p - 1 + slides.length) % slides.length)}
          className="absolute left-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/50 bg-white/75 text-zinc-900 shadow-sm backdrop-blur transition-colors hover:bg-white sm:left-4 sm:h-11 sm:w-11"
          aria-label="Предыдущий слайд"
        >
          <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
        <button
          type="button"
          onClick={() => setActive((p) => (p + 1) % slides.length)}
          className="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/50 bg-white/75 text-zinc-900 shadow-sm backdrop-blur transition-colors hover:bg-white sm:right-4 sm:h-11 sm:w-11"
          aria-label="Следующий слайд"
        >
          <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      </div>
    </section>
  );
}

function CategoryGrid() {
  const { content } = useContent();
  const cats = content.home.categories;

  return (
    <section className="mb-12">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {cats.slice(0, 8).map((c) => (
          <Link
            key={c.slug}
            to={`/catalog/${c.slug}`}
            className="group relative overflow-hidden rounded-xl border border-[#d9d2c6] bg-zinc-50 shadow-[0_14px_28px_rgba(31,26,23,0.08)]"
          >
            <SmartImage
              src={c.image}
              alt={c.title}
              className="h-[210px] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02] sm:h-[240px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/42 via-black/12 to-transparent opacity-90 transition-opacity duration-300 group-hover:opacity-100" />
            <div className="absolute inset-x-0 bottom-0 p-3 sm:p-5">
              <div className="inline-flex max-w-full items-center gap-2 rounded-full bg-white/92 px-3 py-2 text-sm font-semibold text-zinc-900 shadow-sm backdrop-blur sm:gap-3 sm:px-4 sm:py-3">
                {c.title}
                <span className="text-xs font-medium text-zinc-600">Подробнее</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-7 flex justify-center">
        <Link
          to="/catalog"
          className="inline-flex items-center gap-2 rounded-full border border-[#d0c3b2] bg-[#fffaf1] px-5 py-2.5 text-sm font-semibold text-[#3f3831] shadow-sm transition-all hover:translate-y-[-1px] hover:bg-[#f3e8d8]"
        >
          <RefreshCcw className="h-4 w-4" />
          Показать еще
        </Link>
      </div>
    </section>
  );
}

function Benefits() {
  const { content } = useContent();

  return (
    <section className="mb-8">
      <div className="mb-6 text-center">
        <h2 className="section-title text-3xl tracking-tight text-[#231f1a] sm:text-4xl">Основные преимущества</h2>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {content.home.benefits.map((b) => (
          <div key={b.id} className="surface-card rounded-xl p-6">
            <div className="eyebrow">DALINI</div>
            <div className="mt-2 text-base font-semibold text-[#231f1a]">{b.title}</div>
            <div className="mt-2 text-sm leading-6 text-[#5f584f]">{b.text}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function HomePage() {
  useSeo("/");

  return (
    <div>
      <HeroSlider />
      <CategoryGrid />
      <Benefits />
    </div>
  );
}

export function CatalogPage() {
  useSeo("/catalog");
  const { content } = useContent();
  const t = content.uiTexts || {};
  const cats = content.catalog.categories;

  return (
    <div>
      <Breadcrumbs items={[{ label: "Главная", to: "/" }, { label: "Каталог" }]} />
      <h1 className="display-title mb-6 text-4xl text-[#231f1a] sm:mb-8 sm:text-5xl">{t.catalogTitle || "Каталог"}</h1>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cats.map((c) => (
          <Link
            key={c.slug}
            to={`/catalog/${c.slug}`}
            className="group overflow-hidden rounded-xl border border-[#d9d2c6] bg-[#fffdf9] shadow-[0_14px_30px_rgba(31,26,23,0.07)]"
          >
            <SmartImage
              src={c.image}
              alt={c.title}
              className="h-[190px] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02] sm:h-[210px]"
            />
            <div className="p-4">
              <div className="break-words text-sm font-semibold uppercase tracking-[0.08em] text-[#2b2520] sm:tracking-[0.12em]">
                {c.title}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ProductCard({ p }) {
  return (
    <Link
      to={`/product/${p.slug}`}
      className="group overflow-hidden rounded-xl border border-[#d9d2c6] bg-[#fffdf9] shadow-[0_14px_30px_rgba(31,26,23,0.07)]"
    >
      <div className="relative">
        <SmartImage
          src={p.images?.[0]}
          alt={p.title}
          className="h-[200px] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02] sm:h-[220px]"
        />
      </div>
      <div className="p-4">
        <div className="break-words text-sm font-semibold text-zinc-900">{p.title}</div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-base font-semibold text-zinc-900">
            {p.price} {p.currency}
          </span>
          {p.priceOld ? (
            <span className="text-sm text-zinc-500 line-through">
              {p.priceOld} {p.currency}
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

function CategoryChips({ categories, activeSlug }) {
  return (
    <div className="mb-4 flex gap-2 overflow-x-auto pb-2 lg:hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {categories.map((c) => (
        <Link
          key={c.slug}
          to={`/catalog/${c.slug}`}
          className={cn(
            "shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-colors",
            c.slug === activeSlug
              ? "border-[#1f6a54] bg-[#1f6a54] text-white shadow-[0_10px_20px_rgba(31,106,84,0.25)]"
              : "border-[#d0c3b2] bg-[#fff8ee] text-[#4a433c] hover:bg-[#f4e7d3]"
          )}
        >
          {c.title}
        </Link>
      ))}
    </div>
  );
}

export function CategoryPage() {
  const { categorySlug } = useParams();
  const { content } = useContent();
  const category = content.catalog.categories.find((c) => c.slug === categorySlug);

  const products = content.catalog.products.filter((p) => p.categorySlug === categorySlug && p.status === "published");

  useSeo("/catalog");

  if (!category) {
    return (
      <div>
        <Breadcrumbs items={[{ label: "Главная", to: "/" }, { label: "Каталог", to: "/catalog" }, { label: "Не найдено" }]} />
        <h1 className="text-2xl font-semibold">Раздел не найден</h1>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Главная", to: "/" },
          { label: "Каталог", to: "/catalog" },
          { label: category.title },
        ]}
      />

      <div className="grid grid-cols-1 gap-5 lg:gap-8 lg:grid-cols-[280px_1fr]">
        <aside className="hidden rounded-xl border border-[#d7cfbf] bg-[#fffdf9] shadow-[0_14px_30px_rgba(31,26,23,0.06)] lg:sticky lg:top-28 lg:block lg:h-fit">
          <div className="border-b px-5 py-4 text-sm font-semibold text-zinc-900">
            {category.title}
          </div>
          <nav className="divide-y">
            {content.catalog.categories.map((c) => (
              <Link
                key={c.slug}
                to={`/catalog/${c.slug}`}
                className={cn(
                  "block px-5 py-4 text-base transition-colors",
                  c.slug === category.slug
                    ? "bg-[#efe5d8] font-semibold text-[#1f1a17]"
                    : "text-[#4c453d] hover:bg-[#f7f0e6]"
                )}
              >
                {c.title}
              </Link>
            ))}
          </nav>
        </aside>

        <div>
          <CategoryChips categories={content.catalog.categories} activeSlug={category.slug} />
          <h1 className="display-title mb-5 text-4xl text-[#231f1a] sm:mb-6 sm:text-5xl">{category.title}</h1>

          {products.length ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((p) => (
                <ProductCard key={p.id} p={p} />
              ))}
            </div>
          ) : (
            <div className="surface-card rounded-xl p-6 text-sm text-[#595247]">
              Пока нет товаров в этом разделе.
            </div>
          )}
        </div>
      </div>

      <a
        href="https://instagram.com/dalini.by"
        target="_blank"
        rel="noreferrer"
        className="fixed right-2 top-1/2 z-30 hidden -translate-y-1/2 items-center gap-2 rounded-full border border-[#d6cbb9] bg-[#fffdf8] px-3 py-2 text-xs font-semibold text-[#2d2823] shadow-sm md:flex"
        style={{ writingMode: "vertical-rl" }}
      >
        <Instagram className="h-4 w-4" />
        Больше примеров
      </a>
    </div>
  );
}

function Gallery({ images }) {
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    if (!images?.length) return;
    if (active > images.length - 1) setActive(0);
  }, [images, active]);

  if (!images?.length) {
    return (
      <div className="surface-card rounded-xl p-6 text-sm text-[#595247]">
        Изображения пока не добавлены.
      </div>
    );
  }

  return (
    <div>
      <div className="relative overflow-hidden rounded-xl border border-[#d9d2c6] bg-[#fffdf9] shadow-[0_14px_30px_rgba(31,26,23,0.07)]">
        <SmartImage
          src={images?.[active]}
          alt=""
          className="h-[250px] w-full object-cover sm:h-[340px] md:h-[420px]"
        />
        <button
          type="button"
          onClick={() => setActive((p) => (p - 1 + images.length) % images.length)}
          className="absolute left-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/75 text-zinc-900 shadow-sm backdrop-blur transition-colors hover:bg-white sm:left-4 sm:h-11 sm:w-11"
          aria-label="Назад"
        >
          <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
        <button
          type="button"
          onClick={() => setActive((p) => (p + 1) % images.length)}
          className="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/75 text-zinc-900 shadow-sm backdrop-blur transition-colors hover:bg-white sm:right-4 sm:h-11 sm:w-11"
          aria-label="Вперёд"
        >
          <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4 sm:gap-3 md:grid-cols-5">
        {images?.map((src, idx) => (
          <button
            key={src}
            type="button"
            onClick={() => setActive(idx)}
            className={cn(
              "overflow-hidden rounded-md border border-[#ddd4c8] bg-[#fffdf9]",
              idx === active ? "ring-2 ring-zinc-900" : "hover:bg-zinc-50"
            )}
            aria-label={`Фото ${idx + 1}`}
          >
            <SmartImage src={src} alt="" className="h-14 w-full object-cover sm:h-16" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function ProductPage() {
  const { productSlug } = useParams();
  const { content } = useContent();
  const p = content.catalog.products.find((x) => x.slug === productSlug && x.status === "published");

  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const onContact = () => setOpen(true);
    window.addEventListener("dalini:contact", onContact);
    return () => window.removeEventListener("dalini:contact", onContact);
  }, []);

  useSeo("/catalog");

  if (!p) {
    return (
      <div>
        <Breadcrumbs items={[{ label: "Главная", to: "/" }, { label: "Каталог", to: "/catalog" }, { label: "Не найдено" }]} />
        <h1 className="text-2xl font-semibold">Товар не найден</h1>
      </div>
    );
  }

  const cat = content.catalog.categories.find((c) => c.slug === p.categorySlug);

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Главная", to: "/" },
          { label: "Каталог", to: "/catalog" },
          cat ? { label: cat.title, to: `/catalog/${cat.slug}` } : null,
          { label: p.title },
        ].filter(Boolean)}
      />

      <div className="grid grid-cols-1 gap-5 lg:gap-8 lg:grid-cols-[280px_1fr]">
        <aside className="hidden rounded-xl border border-[#d7cfbf] bg-[#fffdf9] shadow-[0_14px_30px_rgba(31,26,23,0.06)] lg:sticky lg:top-28 lg:block lg:h-fit">
          <div className="border-b px-5 py-4 text-sm font-semibold text-zinc-900">
            {cat?.title || "Каталог"}
          </div>
          <nav className="divide-y">
            {content.catalog.categories.map((c) => (
              <Link
                key={c.slug}
                to={`/catalog/${c.slug}`}
                className={cn(
                  "block px-5 py-4 text-base transition-colors",
                  c.slug === cat?.slug
                    ? "bg-[#efe5d8] font-semibold text-[#1f1a17]"
                    : "text-[#4c453d] hover:bg-[#f7f0e6]"
                )}
              >
                {c.title}
              </Link>
            ))}
          </nav>
        </aside>

        <div>
          <CategoryChips categories={content.catalog.categories} activeSlug={cat?.slug} />
          <h1 className="display-title mb-4 text-4xl text-[#231f1a] sm:mb-5 sm:text-5xl">{p.title}</h1>
          <Gallery images={p.images || []} />

          <div className="surface-card mt-5 rounded-xl p-4 sm:mt-6 sm:p-6">
            <div className="flex flex-wrap items-baseline gap-3">
              <div className="text-2xl font-semibold">
                {p.price} {p.currency}
              </div>
              {p.priceOld ? (
                <div className="text-lg text-zinc-500 line-through">
                  {p.priceOld} {p.currency}
                </div>
              ) : null}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div className="text-sm font-semibold">Выбрать материал:</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(p.options?.material || []).map((m) => (
                    <button
                      key={m}
                      type="button"
                      className="rounded-full border border-[#d2c8b8] bg-[#fff8ed] px-4 py-2 text-sm text-[#403932] transition-colors hover:bg-[#f1e5d3]"
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold">Выбрать размер:</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(p.options?.size || []).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="rounded-full border border-[#d2c8b8] bg-[#fff8ed] px-4 py-2 text-sm text-[#403932] transition-colors hover:bg-[#f1e5d3]"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button onClick={() => setOpen(true)} className="w-full rounded-none bg-[#1f6a54] text-white hover:bg-[#195642] sm:w-auto">
                Оставить заявку
              </Button>
              <Button asChild variant="secondary" className="w-full rounded-none sm:w-auto">
                <Link to="/catalog">В каталог</Link>
              </Button>
              <a
                href="https://instagram.com/dalini.by"
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-none border border-[#d2c7b6] bg-[#fff8ed] px-4 py-2 text-sm font-medium text-[#2f2923] hover:bg-[#efe1cd] sm:ml-auto sm:w-auto sm:justify-start"
              >
                <Instagram className="h-4 w-4" />
                Больше примеров
              </a>
            </div>
          </div>

          <div className="mt-7 whitespace-pre-line break-words text-sm leading-7 text-zinc-700 sm:mt-8">{p.description}</div>

          <div className="surface-card mt-8 rounded-xl">
            <div className="border-b px-4 py-3 text-sm font-semibold sm:px-6 sm:py-4">
              Характеристики
            </div>
            <div className="divide-y">
              {(p.characteristics || []).map((row) => (
                <div
                  key={row.k}
                  className="grid grid-cols-1 gap-1 px-4 py-3 text-sm sm:grid-cols-2 sm:gap-4 sm:px-6"
                >
                  <div className="text-zinc-600">{row.k}</div>
                  <div className="text-zinc-900">{row.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <a
        href="https://instagram.com/dalini.by"
        target="_blank"
        rel="noreferrer"
        className="fixed right-2 top-1/2 z-30 hidden -translate-y-1/2 items-center gap-2 rounded-full border border-[#d6cbb9] bg-[#fffdf8] px-3 py-2 text-xs font-semibold text-[#2d2823] shadow-sm md:flex"
        style={{ writingMode: "vertical-rl" }}
      >
        <Instagram className="h-4 w-4" />
        Больше примеров
      </a>

      <ContactDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

export function AboutPage() {
  useSeo("/about");
  const { content } = useContent();
  const t = content.uiTexts || {};
  const page = content.pages.about;
  const aboutText = (page.blocks || [])
    .map((b) => b.text?.trim())
    .filter(Boolean)
    .join("\n\n");

  return (
    <div>
      <Breadcrumbs items={[{ label: "Главная", to: "/" }, { label: "О компании" }]} />
      <h1 className="display-title mb-5 text-4xl text-[#231f1a] sm:mb-6 sm:text-5xl">{page.title || t.aboutPageTitleFallback || "О компании"}</h1>

      <div className="surface-card rounded-xl px-4 py-4 text-sm leading-8 text-[#4e463f] sm:px-6 sm:py-6 sm:text-[15px]">
        <div className="whitespace-pre-line">{aboutText}</div>
      </div>
    </div>
  );
}

export function ContactsPage() {
  useSeo("/contacts");
  const { content } = useContent();
  const t = content.uiTexts || {};
  const company = content.company || COMPANY_INFO;
  const [open, setOpen] = React.useState(false);
  const productionAddress = (content.contacts.address || "").trim() || "Калодищи ул.Тюленина 10";
  const mapLat = 53.9539947;
  const mapLng = 27.7856496;
  const mapSrc = `https://yandex.by/map-widget/v1/?ll=${mapLng}%2C${mapLat}&z=16&l=map&pt=${mapLng},${mapLat},pm2rdm&text=${encodeURIComponent(productionAddress)}`;

  React.useEffect(() => {
    const onContact = () => setOpen(true);
    window.addEventListener("dalini:contact", onContact);
    return () => window.removeEventListener("dalini:contact", onContact);
  }, []);

  return (
    <div>
      <Breadcrumbs items={[{ label: "Главная", to: "/" }, { label: "Контакты" }]} />
      <h1 className="display-title mb-5 text-4xl text-[#231f1a] sm:mb-6 sm:text-5xl">{t.contactPageTitle || "Контакты"}</h1>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="surface-card rounded-xl p-4 sm:p-5">
          <div className="text-sm font-semibold text-zinc-900">Реквизиты компании</div>
          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 text-[13px] leading-5 text-zinc-700 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">Наименование:</dt>
              <dd className="ml-1 inline font-medium text-zinc-900">{company.legalName || COMPANY_INFO.legalName}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">Юр. адрес:</dt>
              <dd className="ml-1 inline">{company.address || COMPANY_INFO.address}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">Адрес шоурума и производства:</dt>
              <dd className="ml-1 inline">{productionAddress}</dd>
            </div>
            <div>
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">УНП:</dt>
              <dd className="ml-1 inline font-medium text-zinc-900">{company.unp || COMPANY_INFO.unp}</dd>
            </div>
            <div>
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">ОКПО:</dt>
              <dd className="ml-1 inline font-medium text-zinc-900">{company.okpo || COMPANY_INFO.okpo}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">IBAN:</dt>
              <dd className="ml-1 inline break-all font-medium text-zinc-900">{company.iban || COMPANY_INFO.iban}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">Банк:</dt>
              <dd className="ml-1 inline">{company.bank || COMPANY_INFO.bank}</dd>
            </div>
            <div>
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">SWIFT:</dt>
              <dd className="ml-1 inline font-medium text-zinc-900">{company.swift || COMPANY_INFO.swift}</dd>
            </div>
            <div>
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">Директор:</dt>
              <dd className="ml-1 inline font-medium text-zinc-900">{company.director || COMPANY_INFO.director}</dd>
            </div>
            <div>
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">E-mail:</dt>
              <dd className="ml-1 inline">
                <a className="break-all text-zinc-900 underline-offset-4 hover:underline" href={`mailto:${company.email || COMPANY_INFO.email}`}>
                  {company.email || COMPANY_INFO.email}
                </a>
              </dd>
            </div>
            <div>
              <dt className="inline text-[11px] uppercase tracking-[0.07em] text-zinc-500">Телефон:</dt>
              <dd className="ml-1 inline">
                <a className="text-zinc-900 underline-offset-4 hover:underline" href={`tel:${(company.phone || COMPANY_INFO.phone).replace(/\s|\(|\)/g, "")}`}>
                  {company.phone || COMPANY_INFO.phone}
                </a>
              </dd>
            </div>
          </dl>

          <div className="mt-4">
            <Button onClick={() => setOpen(true)}>Связаться с менеджером</Button>
          </div>
        </div>

        <div className="surface-card rounded-xl p-4 sm:p-6">
          <div className="text-sm font-semibold text-zinc-900">Карта</div>
          <div className="mt-3 overflow-hidden rounded border bg-zinc-50">
            <iframe
              title="Карта шоурума DALINI"
              src={mapSrc}
              width="100%"
              height="280"
              frameBorder="0"
              allowFullScreen
              loading="lazy"
              className="block sm:h-[320px]"
            />
          </div>
        </div>
      </div>

      <ContactDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

export function NotFoundPage() {
  useSeo("/404");
  const { content } = useContent();
  const t = content.uiTexts || {};
  return (
    <div>
      <Breadcrumbs items={[{ label: "Главная", to: "/" }, { label: "Ошибка 404" }]} />
      <h1 className="display-title mb-2 text-4xl text-[#231f1a] sm:text-5xl">{t.notFoundTitle || "Ошибка 404"}</h1>
      <p className="text-sm text-zinc-700">
        {t.notFoundDescription || "Данная страница не существует. Попробуйте перейти на другую страницу сайта."}
      </p>
      <div className="mt-6">
        <Button asChild>
          <Link to="/">Главная</Link>
        </Button>
      </div>
    </div>
  );
}
